import { useEffect } from 'react';
import { useSearchParams } from 'react-router';
import { Button, Spinner } from 'components';
import { useTranslation } from 'react-i18next';
import {
  Container,
  PopupCard,
  Content,
  Footer,
  CenterStack,
  Title,
  TitleSpacer,
  Message,
  HeaderContainer,
} from './progressIndicationPopup.styles';

type ProgressIndicationPopupVariant = 'loading' | 'success' | 'failure';

export default function ProgressIndicationPopupScreen() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const variant = (searchParams.get('variant') || 'loading') as ProgressIndicationPopupVariant;
  const paramMessage = searchParams.get('message');

  const message = paramMessage || t(`popup.messages.${variant}`);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        void window.electronAPI?.closeFloatingPopup?.();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const handleClose = () => {
    void window.electronAPI?.closeFloatingPopup?.();
  };

  return (
    <Container>
      <PopupCard>
        <Content>
          <CenterStack role="alert" aria-live="assertive">
            <HeaderContainer>
              {variant === 'loading' ? (
                <>
                  <Title>{t('popup.loadingTitle')}</Title>
                  <Spinner size="sm" />
                </>
              ) : variant === 'success' ? (
                <>
                  <TitleSpacer>.</TitleSpacer>
                  <CheckMark />
                </>
              ) : (
                <>
                  <CrossMark />
                  <Title>{t('popup.failureTitle')}</Title>
                </>
              )}
            </HeaderContainer>
            <Message>{message}</Message>
          </CenterStack>
        </Content>
        {(variant === 'loading' || variant === 'success' || variant === 'failure') && (
          <Footer>
            {variant === 'loading' ? (
              <Button onClick={handleClose} className="bg-destructive hover:opacity-90 text-white">
                {t('popup.cancel')}
              </Button>
            ) : variant === 'failure' ? (
              <Button onClick={handleClose} className="bg-destructive hover:opacity-90 text-white">
                {t('popup.close')}
              </Button>
            ) : (
              <Button onClick={handleClose}>{t('popup.close')}</Button>
            )}
          </Footer>
        )}
      </PopupCard>
    </Container>
  );
}

const CheckMark = () => {
  return (
    <span
      className="w-[34px] h-[34px] rounded-full flex items-center justify-center text-lg font-semibold text-white bg-[#10b981] shadow-lg"
      aria-hidden="true"
    >
      ✓
    </span>
  );
};

const CrossMark = () => {
  return (
    <span
      className="w-[34px] h-[34px] rounded-full flex items-center justify-center text-lg font-semibold text-white bg-[#ef4444] shadow-lg"
      aria-hidden="true"
    >
      ×
    </span>
  );
};
